<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-order-list">
        <i class="far fa-list"></i> DIFOT Order List
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-order-list">
        <DataTable ref="dataTable" :columns="columnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="9" :url="dataURL" :filterOptions="orderFilterOptions" :displayTools="true" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Orders" :useQueryData="useQueryData" :debounceTime="300">
          <template v-slot:title>
            <slot name="title" />
          </template>
          <template v-slot:tools>
            <slot name="tools" />
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" :newTab="openNewTab" />
              </td>
              <td>
                {{row.data.contactName}}
              </td>
              <td>
                {{row.data.customerRef | formatHyphenEmptyValue}}
              </td>
              <td>
                <LabelBadge :labelClass="`label-order-type-${row.data.type}`">{{row.data.type | formatLookupName(OrderTypeLookup)}}</LabelBadge>
              </td>
              <td>
                <LabelBadge labelClass="label-despatch-badge">{{row.data.despatchTaskTypeName}}</LabelBadge>
              </td>
              <td>
                <LabelTags :labelList="row.data.deliveryTag" class="d-inline-block" />
              </td>
              <td>{{row.data.dateOrderCreated | formatDate}}</td>
              <td>{{row.data.dateDespatchStarted | formatDate}}</td>
              <td>{{row.data.dateCurrentlyDue | formatDate}}</td>
              <td>{{row.data.dateClosed | formatDate}}</td>
              <td><LabelBadge :labelClass="`label-difot-code-${row.data.operationalReportingCode}`">{{formatLookupName(row.data.operationalReportingCode, ItemOperationalReportingCodeLookup)}}</LabelBadge></td>
              <td>{{formatHyphenEmptyValue(row.data.operationalReportingNote)}}</td>
              <td><LabelBadge :labelClass="`label-difot-result-${row.data.operationalReportingResultType}`">{{formatLookupName(row.data.operationalReportingResultType, OperationalReportingResultTypeLookup)}}</LabelBadge></td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'

import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'
import OrderProductionSummaryPopover from '@/modules/orders/components/orderProductionSummaryPopover'

import { OrderTypes, OrderTypeLookup, OrderStatusTypes, ItemOperationalReportingCodes, ItemOperationalReportingCodeLookup, OperationalReportingResultTypes, OperationalReportingResultTypeLookup } from '@/store/types'

export default {
  name: "OrderDifotList",
  components: {
    LabelOrderStatusBadge,
    OrderProductionSummaryPopover
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    useQueryData: Boolean,
    filterContactId: Number,
    filterType: String
  },
  created() {
    this.OrderTypeLookup = OrderTypeLookup
    this.ItemOperationalReportingCodeLookup = ItemOperationalReportingCodeLookup
    this.OperationalReportingResultTypeLookup = OperationalReportingResultTypeLookup
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          status: OrderStatusTypes.completed,
          hasOperationalReporting: 1
        }
      }
      if(this.filterContactId)
        params.filter.contactId = this.filterContactId

      return '/orders?' + $.param(params)
    },
    orderFilterOptions() {
      var filterOptions = [
        {
          field: 'type',
          name: 'Type',
          columnIndex: 0,
          options: [
            { name: 'Order', value: OrderTypes.order, class: 'label-order-type-order' },
            { name: 'NCR', value: OrderTypes.nonChargeableRework, class: 'label-order-type-nonChargeableRework' }
          ]
        },
        {
          field: 'despatchTaskTypeId',
          name: 'Despatch Task Type',
          columnIndex: 0,
          options: this.taskTypeList.map(taskType => {
            return {
              name: taskType.name,
              value: taskType.id,
              class: 'label-despatch-badge'
            }
          })
        }
      ]

      let deliveryTagLabels = this.$store.getters['label/getLabelsByType']('contactAddressDelivery')
      filterOptions.push({
        name: 'Despatch Tag',
        field: 'deliveryTagName',   // The extracted 'name' component from the order delivery tag
        columnIndex: 0,
        options: deliveryTagLabels.map(labelInfo => {
          return {
            name: labelInfo.name,
            value: labelInfo.name,
            colour: labelInfo.colour
          }
        })
      })

      // Reporting codes
      filterOptions.push({
        name: 'Report Code',
        field: 'operationalReportingCode',
        columnIndex: 1,
        options: ItemOperationalReportingCodeLookup.map(info => {
          return {
            name: info.name,
            value: info.value,
            class: `label-difot-code-${info.value}`
          }
        })
      })

      // Reporting results
      filterOptions.push({
        name: 'Report Result',
        field: 'operationalReportingResultType',
        columnIndex: 2,
        options: OperationalReportingResultTypeLookup.map(info => {
          return {
            name: info.name,
            value: info.value,
            class: `label-difot-result-${info.value}`
          }
        })
      })

      return filterOptions
    }
  },
  data() {
    return {
      columnList: [
        { title: "Number", data: "sequenceNumber" },
        { title: "Contact", data: "contactName" },
        { title: "Reference No.", data: "customerRef" },
        { title: "Type", data: "type" },
        { title: "Despatch", data: "despatchTaskTypeName" },
        { title: "Despatch Tag", data: "deliveryTag" },
        { title: "Created", data: "dateOrderCreated", dateFilter: true, type: 'datetime' },
        { title: "Despatch Started", data: "dateDespatchStarted", dateFilter: true, type: 'datetime' },
        { title: "Estimated Completion", data: "dateCurrentlyDue", dateFilter: true, type: 'datetime' },
        { title: "Completed", data: "dateClosed", dateFilter: true, type: 'datetime' },
        { title: "Report Code", data: "operationalReportingCode" },
        { title: "Report Note", data: "operationalReportingNote" },
        { title: "Report Result", data: "operationalReportingResultType" }
      ],
      taskTypeList: []
    }
  },
  mounted() {
    this.requestFilterLabels()
  },
  methods: {
    reload() {
      this.$refs.dataTable.reload()
    },
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {

          // Get labels / Task types for the filter popover
          var results = await Promise.all([
            this.$apiService.coreService.getLabelsForType('contactAddressDelivery'),
            this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 }})
          ])

          let deliveryTagLabels = results[0].data
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'contactAddressDelivery',
            labelList: deliveryTagLabels
          })
          this.taskTypeList = results[1].data

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    }
  }
};
</script>
